import React, { useEffect, useState, useMemo } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CreatableSelect from "react-select/creatable";
import { useNavigate } from "react-router-dom";
import * as categoriesCrud from "../../../_redux/categories/categoriesCrud";

// Validation schema
const CategoryEditSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Minimum 2 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Name is required"),
});

let cachedFormCategories = null;

export function CreateCategoryForm({
  category,
  btnRef,
  saveCategory,
}) {
  const navigate = useNavigate();
  const [allCategories, setAllCategories] = useState(() => cachedFormCategories || []);
  const [loadingCategories, setLoadingCategories] = useState(!cachedFormCategories);
  const [selectedExistingCategory, setSelectedExistingCategory] = useState(null);

  useEffect(() => {
    if (cachedFormCategories && cachedFormCategories.length > 0) {
      setAllCategories(cachedFormCategories);
      setLoadingCategories(false);
      return;
    }

    setLoadingCategories(true);
    categoriesCrud
      .findCategories({
        pageNumber: 1,
        pageSize: 3000,
        filter: {},
        sortField: "name",
        sortOrder: "asc",
      })
      .then((res) => {
        const entities = res.data?.entities || [];
        cachedFormCategories = entities;
        setAllCategories(entities);
        setLoadingCategories(false);
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
        setLoadingCategories(false);
      });
  }, []);

  const options = useMemo(() => {
    return (allCategories || []).map((c) => ({
      value: String(c.id),
      label: c.name,
      raw: c,
    }));
  }, [allCategories]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={category}
        validationSchema={CategoryEditSchema}
        onSubmit={(values) => {
          saveCategory(values);
        }}
      >
        {({ handleSubmit, setFieldValue, values, errors, touched, handleBlur }) => {
          const handleSelectChange = (opt) => {
            if (opt && opt.raw) {
              setFieldValue("name", opt.raw.name);
              setSelectedExistingCategory(opt.raw);
            } else if (opt) {
              setFieldValue("name", opt.label);
              const found = allCategories.find(
                (c) => (c.name || "").trim().toLowerCase() === opt.label.trim().toLowerCase()
              );
              setSelectedExistingCategory(found || null);
            } else {
              setFieldValue("name", "");
              setSelectedExistingCategory(null);
            }
          };

          const handleCreateOption = (inputValue) => {
            const trimmed = (inputValue || "").trim();
            setFieldValue("name", trimmed);
            const found = allCategories.find(
              (c) => (c.name || "").trim().toLowerCase() === trimmed.toLowerCase()
            );
            setSelectedExistingCategory(found || null);
          };

          const selectedOption = values.name
            ? selectedExistingCategory
              ? {
                  value: String(selectedExistingCategory.id),
                  label: selectedExistingCategory.name,
                  raw: selectedExistingCategory,
                }
              : { value: values.name, label: values.name }
            : null;

          return (
            <Form className="form form-label-right">
              <div className="form-group row">
                <div className="col-lg-6">
                  <label className="font-weight-bold text-dark">
                    Category Name <span className="text-danger">*</span>
                  </label>

                  <CreatableSelect
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    options={options}
                    value={selectedOption}
                    onChange={handleSelectChange}
                    onCreateOption={handleCreateOption}
                    isLoading={loadingCategories}
                    placeholder="Type category name to search or create..."
                    formatCreateLabel={(inputValue) => `+ Create new category "${inputValue}"`}
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue
                        ? "Type to search existing categories..."
                        : `No existing category matching "${inputValue}". Press Enter to create.`
                    }
                    autoFocus
                  />

                  <span className="form-text text-muted font-size-xs mt-1">
                    Start writing to see previously added categories. If not found, select "+ Create new category" to add it.
                  </span>

                  {errors.name && touched.name ? (
                    <div className="text-danger mt-1 font-size-sm">{errors.name}</div>
                  ) : null}

                  {/* Existing category alert */}
                  {selectedExistingCategory && (
                    <div className="alert alert-custom alert-light-warning p-3 mt-3 font-size-sm">
                      <div className="alert-icon">
                        <i className="flaticon-warning text-warning"></i>
                      </div>
                      <div className="alert-text">
                        <div>
                          A category named <strong>"{selectedExistingCategory.name}"</strong> already exists in the system.
                        </div>
                        <div className="mt-2 d-flex align-items-center">
                          <span
                            className={`badge mr-2 ${
                              selectedExistingCategory.status === "active"
                                ? "badge-light-success"
                                : "badge-light-danger"
                            }`}
                          >
                            {selectedExistingCategory.status || "active"}
                          </span>
                          <button
                            type="button"
                            className="btn btn-xs btn-primary font-weight-bold"
                            onClick={() =>
                              navigate(`/category/${selectedExistingCategory.id}/edit`)
                            }
                          >
                            <i className="flaticon2-pen mr-1 font-size-xs"></i>
                            Edit Existing Category
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* New category name ready to save */}
                  {values.name && !selectedExistingCategory && (
                    <div className="alert alert-custom alert-light-success p-2 mt-3 font-size-sm">
                      <div className="alert-icon">
                        <i className="flaticon2-checkmark text-success"></i>
                      </div>
                      <div className="alert-text font-weight-bold">
                        New category name available: "{values.name}". Click Save to create it.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                style={{ display: "none" }}
                ref={btnRef}
                onSubmit={() => handleSubmit()}
              ></button>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
